import React, { useState, useEffect, useCallback } from 'react';
import { publicFetch } from '../utils/publicApi';
import PortalTrialSessions from '../swishiq/PortalTrialSessions';

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

export default function PublicCoachRoster({ token, weekOffset, onAddParticipant }) {
  const [programs, setPrograms] = useState([]);
  const [totalPay, setTotalPay] = useState(0);
  const [attendedPay, setAttendedPay] = useState(0);
  const [cutoffHours, setCutoffHours] = useState(24);
  const [responseEnabled, setResponseEnabled] = useState(true);
  const [payVisible, setPayVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  // Confirmation modal shown when ticking a NOT-financial participant.
  // Holds {prog, participant} so the buttons know what to act on.
  const [notFinancialPrompt, setNotFinancialPrompt] = useState(null);
  // "Select Other Participants" modal — lets the coach toggle a participant
  // who's enrolled in the program type (or a feeder) but isn't currently
  // selected for this specific session. Holds {prog, participants[], loading}.
  const [otherPartsModal, setOtherPartsModal] = useState(null);
  const [otherPartsBusy, setOtherPartsBusy] = useState(false);
  // Per-program collapsed-state. Defaults to empty (every session starts
  // with participants HIDDEN); coaches expand only the session they're
  // working on so the roster stays compact.
  const [expandedParticipants, setExpandedParticipants] = useState(() => new Set());
  const toggleParticipantsExpanded = (programId) => {
    setExpandedParticipants(prev => {
      const next = new Set(prev);
      if (next.has(programId)) next.delete(programId); else next.add(programId);
      return next;
    });
  };
  const [error, setError] = useState('');

  const formatLocalDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const getDateRange = useCallback(() => {
    const today = new Date();
    const day = today.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diffToMonday + (weekOffset * 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return {
      startDate: formatLocalDate(monday),
      endDate: formatLocalDate(sunday)
    };
  }, [weekOffset]);

  useEffect(() => {
    const loadPrograms = async () => {
      try {
        setLoading(true);
        setError('');
        const { startDate, endDate } = getDateRange();
        const res = await publicFetch(`${API_BASE}/public/roster/coaching/${token}?startDate=${startDate}&endDate=${endDate}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setPrograms(data.programs || []);
          setTotalPay(data.totalPay || 0);
          setAttendedPay(data.attendedPay || 0);
          if (data.cutoffHours !== undefined) setCutoffHours(data.cutoffHours);
          if (data.responseEnabled !== undefined) setResponseEnabled(data.responseEnabled);
          if (data.payVisible !== undefined) setPayVisible(data.payVisible);
        } else {
          setError('Failed to load coaching roster');
        }
      } catch (err) {
        setError('Error loading coaching roster: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadPrograms();
  }, [token, weekOffset, getDateRange]);

  const formatLongDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-AU', {
      weekday: 'long', day: 'numeric', month: 'long'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  };

  const isDayOnOrAfter = (date) => {
    if (!date) return false;
    const today = new Date();
    const todayStr = formatLocalDate(today);
    return todayStr >= date;
  };

  const isWithinCutoff = (prog) => {
    if (!prog.date || !prog.startTime) return false;
    const [h, m] = prog.startTime.split(':').map(Number);
    const programTime = new Date(prog.date + 'T00:00:00');
    programTime.setHours(h, m, 0, 0);
    const cutoffTime = new Date(programTime.getTime() - cutoffHours * 60 * 60 * 1000);
    return new Date() >= cutoffTime;
  };

  const canRespond = (prog) => {
    if (prog.isLocked) return false;
    if (prog.isPast) return false;
    if (isWithinCutoff(prog)) return false;
    return true;
  };

  const handleRespond = async (prog, response) => {
    if (response === 'rejected') {
      if (!window.confirm('Are you sure you want to reject this shift?')) return;
    }
    try {
      const res = await publicFetch(`${API_BASE}/public/roster/coaching/${prog.assignmentId}/respond/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ response })
      });
      if (res.ok) {
        setPrograms(prev => prev.map(p =>
          p.assignmentId === prog.assignmentId ? { ...p, response } : p
        ));
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to submit response');
      }
    } catch (err) {
      alert('Error submitting response: ' + err.message);
    }
  };

  // Actually post the attendance flip and update local state. Used by the
  // toggle for the simple cases AND by the not-financial-prompt buttons.
  const writeAttendance = async (programId, participantId, attended) => {
    try {
      const res = await publicFetch(`${API_BASE}/public/programs/${programId}/participants/${participantId}/attendance/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ attended })
      });
      if (res.ok) {
        setPrograms(prev => prev.map(p => {
          if (p.programId !== programId) return p;
          return {
            ...p,
            participants: p.participants.map(part =>
              part.participantId === participantId ? { ...part, attended } : part
            )
          };
        }));
      }
    } catch (err) {
      console.error('Error updating participant attendance:', err);
    }
  };

  const handleParticipantAttendance = (prog, participant, currentStatus) => {
    const newStatus = !currentStatus;
    // When marking a NOT-financial participant as ATTENDED, open the
    // 3-button confirmation modal (Checkin and Notify / Checkin / Cancel).
    if (newStatus && participant && participant.isFinancial === false) {
      setNotFinancialPrompt({ prog, participant });
      return;
    }
    writeAttendance(prog.programId, participant.participantId, newStatus);
  };

  const sendSignupReminder = async (prog, participant) => {
    try {
      const res = await publicFetch(
        `${API_BASE}/public/programs/${prog.programTypeId}/participants/${participant.participantId}/signup-reminder/${token}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        alert(`Couldn't send reminder SMS: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      alert('Couldn\'t send reminder SMS: ' + err.message);
    }
  };

  const openOtherParticipantsModal = async (prog) => {
    setOtherPartsModal({ prog, participants: [], loading: true });
    try {
      const res = await publicFetch(`${API_BASE}/public/programs/${prog.programId}/participants/${token}`, { credentials: 'include' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setOtherPartsModal({ prog, participants: [], loading: false, error: data.error || `HTTP ${res.status}` });
        return;
      }
      const data = await res.json();
      setOtherPartsModal({ prog, participants: data, loading: false });
    } catch (err) {
      setOtherPartsModal({ prog, participants: [], loading: false, error: err.message });
    }
  };

  const toggleOtherParticipant = async (participant) => {
    if (!otherPartsModal) return;
    setOtherPartsBusy(true);
    try {
      const res = await publicFetch(
        `${API_BASE}/public/programs/${otherPartsModal.prog.programId}/participants/${participant.id}/toggle/${token}`,
        { method: 'POST' }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.error || `Couldn't update selection (HTTP ${res.status})`);
        return;
      }
      // Optimistic local update of the modal list.
      setOtherPartsModal(prev => prev ? {
        ...prev,
        participants: prev.participants.map(p =>
          p.id === participant.id ? { ...p, selected: data.selected ? 1 : 0 } : p
        )
      } : prev);
      // Also reflect on the underlying session list so the chip appears /
      // disappears without a full reload.
      setPrograms(prev => prev.map(p => {
        if (p.programId !== otherPartsModal.prog.programId) return p;
        const partsList = p.participants || [];
        if (data.selected) {
          if (partsList.some(x => x.participantId === participant.id)) return p;
          return {
            ...p,
            participants: [
              ...partsList,
              {
                participantId: participant.id,
                firstName: participant.firstName,
                lastName: participant.lastName,
                attended: false,
                isFinancial: null
              }
            ]
          };
        }
        return { ...p, participants: partsList.filter(x => x.participantId !== participant.id) };
      }));
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setOtherPartsBusy(false);
    }
  };

  const handleNotFinancialCheckin = async ({ notify }) => {
    if (!notFinancialPrompt) return;
    const { prog, participant } = notFinancialPrompt;
    setNotFinancialPrompt(null);
    if (notify) {
      if (prog.programSignupUrl) {
        await sendSignupReminder(prog, participant);
      } else {
        alert(`Can't send reminder: ${prog.programType || 'this program'} has no signup URL set in Admin → Coaching Programs.`);
      }
    }
    await writeAttendance(prog.programId, participant.participantId, true);
  };

  const getCardClass = (prog) => {
    if (prog.response === 'rejected') return 'public-program-card public-program-rejected';
    if (prog.response === 'accepted') return 'public-program-card public-program-accepted';
    if (prog.isPast && prog.isLocked && !prog.attended) return 'public-program-card public-program-unattended';
    return 'public-program-card';
  };

  // Group programs by date
  const groupedByDate = {};
  programs.forEach(prog => {
    if (!groupedByDate[prog.date]) groupedByDate[prog.date] = [];
    groupedByDate[prog.date].push(prog);
  });
  const sortedDates = Object.keys(groupedByDate).sort();

  return (
    <div>
      {/* NOT-financial check-in confirmation modal (My Portal) */}
      {/* SELECT OTHER PARTICIPANTS modal (My Portal) */}
      {otherPartsModal && (() => {
        const prog = otherPartsModal.prog;
        const programName = prog.programType || 'this program';
        const list = otherPartsModal.participants || [];
        return (
          <div
            role="dialog"
            aria-modal="true"
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '1rem' }}
            onClick={() => setOtherPartsModal(null)}
          >
            <div
              style={{ background: 'white', borderRadius: 10, padding: '1.4rem 1.5rem', maxWidth: 520, width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#A4161A' }}>Select Other Participants</h3>
                <button
                  type="button"
                  onClick={() => setOtherPartsModal(null)}
                  style={{ background: 'transparent', border: 'none', fontSize: '1.4rem', lineHeight: 1, cursor: 'pointer', color: '#888' }}
                  title="Close"
                >&times;</button>
              </div>
              <p style={{ margin: '0 0 0.8rem 0', fontSize: '0.85rem', color: '#555' }}>
                These participants are enrolled in <strong>{programName}</strong> but in a different
                Registration Group from this session. Tick to add them as an off-roster extra to
                this session. Untick to remove (only allowed if not yet attended).
              </p>
              {(() => {
                // Show only participants NOT in this session's Reg Group —
                // the modal is for off-roster adds. Already-added off-roster
                // people stay visible (they have selected=true) so they can
                // be unticked to remove. Sorted alphabetically by firstName
                // then lastName so coaches who know players by first name
                // can scan quickly (matches the desktop modal).
                const others = list
                  .filter(p => p.inRegistrationGroup === false)
                  .slice()
                  .sort((a, b) => {
                    const fa = (a.firstName || '').toLowerCase();
                    const fb = (b.firstName || '').toLowerCase();
                    if (fa !== fb) return fa < fb ? -1 : 1;
                    const la = (a.lastName || '').toLowerCase();
                    const lb = (b.lastName || '').toLowerCase();
                    return la < lb ? -1 : la > lb ? 1 : 0;
                  });
                if (otherPartsModal.loading) {
                  return <div style={{ padding: '1rem', color: '#888', fontSize: '0.9rem' }}>Loading…</div>;
                }
                if (otherPartsModal.error) {
                  return (
                    <div style={{ padding: '0.6rem 0.8rem', background: '#ffebee', border: '1px solid #ef9a9a', borderRadius: 4, color: '#c62828', fontSize: '0.85rem' }}>
                      {otherPartsModal.error}
                    </div>
                  );
                }
                if (list.length === 0) {
                  return <div style={{ padding: '1rem', color: '#888', fontSize: '0.9rem' }}>No participants are enrolled in {programName} yet.</div>;
                }
                if (others.length === 0) {
                  return (
                    <div style={{ padding: '1rem', color: '#888', fontSize: '0.9rem' }}>
                      Everyone enrolled in {programName} is already in this session's Registration Group.
                      There are no off-roster participants to add.
                    </div>
                  );
                }
                return (
                  <div style={{ border: '1px solid #ddd', borderRadius: 6, maxHeight: '50vh', overflowY: 'auto' }}>
                    {others.map(p => {
                      const checked = !!p.selected;
                      return (
                        <label
                          key={p.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '0.6rem',
                            padding: '0.55rem 0.7rem', borderBottom: '1px solid #f1f1f1',
                            cursor: otherPartsBusy ? 'wait' : 'pointer', minHeight: 44
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={otherPartsBusy || (checked && !!p.attended)}
                            onChange={() => toggleOtherParticipant(p)}
                            style={{ width: 20, height: 20, accentColor: '#2e7d32' }}
                          />
                          <span style={{ flex: 1, fontSize: '0.95rem' }}>
                            <strong>{p.firstName} {p.lastName}</strong>
                            {p.attended ? <span style={{ marginLeft: 8, color: '#2e7d32', fontSize: '0.75rem', fontWeight: 600 }}>ATTENDED</span> : null}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                );
              })()}
              <div style={{ marginTop: '0.9rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setOtherPartsModal(null)}
                  style={{ padding: '0.5rem 1rem', background: '#1565c0', border: '1px solid #0d47a1', color: 'white', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {notFinancialPrompt && (() => {
        const { prog, participant } = notFinancialPrompt;
        const displayName = `${participant.firstName} ${participant.lastName}`;
        const programName = prog.programType || 'this program';
        const noSignupUrl = !prog.programSignupUrl;
        return (
          <div
            role="dialog"
            aria-modal="true"
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '1rem' }}
            onClick={() => setNotFinancialPrompt(null)}
          >
            <div
              style={{ background: 'white', borderRadius: 10, padding: '1.4rem 1.5rem', maxWidth: 460, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
              onClick={e => e.stopPropagation()}
            >
              <h3 style={{ margin: '0 0 0.7rem 0', color: '#c62828', fontSize: '1.05rem' }}>Not financial</h3>
              <p style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '0.95rem', lineHeight: 1.5 }}>
                <strong>{displayName}</strong> is NOT financial for <strong>{programName}</strong>.
              </p>
              <ul style={{ margin: '0 0 1rem 1.1rem', padding: 0, color: '#444', fontSize: '0.88rem', lineHeight: 1.5 }}>
                <li>Select <strong>Checkin and Notify</strong> to mark as attended AND send a signup reminder SMS.</li>
                <li>Select <strong>Checkin</strong> to mark as attended only.</li>
                <li>Select <strong>Cancel</strong> to abort.</li>
              </ul>
              {noSignupUrl && (
                <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderLeft: '3px solid #f9a825', padding: '0.5rem 0.75rem', borderRadius: 4, marginBottom: '1rem', fontSize: '0.8rem', color: '#7f5e00' }}>
                  No signup URL is set for this Coaching Program — Checkin and Notify is unavailable. Ask an admin to set one in Admin → Coaching Programs.
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setNotFinancialPrompt(null)}
                  style={{ padding: '0.5rem 1rem', background: '#fff', border: '1px solid #999', color: '#555', borderRadius: 4, cursor: 'pointer', fontWeight: 500 }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleNotFinancialCheckin({ notify: false })}
                  style={{ padding: '0.5rem 1rem', background: '#1565c0', border: '1px solid #0d47a1', color: 'white', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                >
                  Checkin
                </button>
                <button
                  onClick={() => handleNotFinancialCheckin({ notify: true })}
                  disabled={noSignupUrl}
                  title={noSignupUrl ? 'No signup URL set for this Coaching Program' : 'Mark attended AND send signup-reminder SMS'}
                  style={{
                    padding: '0.5rem 1rem',
                    background: noSignupUrl ? '#aaa' : '#2e7d32',
                    border: '1px solid ' + (noSignupUrl ? '#888' : '#1b5e20'),
                    color: 'white',
                    borderRadius: 4,
                    cursor: noSignupUrl ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  Checkin and Notify
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {error && <div className="public-tab-error">{error}</div>}

      <PortalTrialSessions token={token} startDate={getDateRange().startDate} endDate={getDateRange().endDate} />

      {loading ? (
        <div className="public-tab-loading">Loading coaching roster...</div>
      ) : programs.length === 0 ? (
        <div className="public-empty-state">
          <p>No coaching assignments for this period</p>
        </div>
      ) : (
        <>
          {sortedDates.map(date => (
            <div key={date} className="public-date-section">
              <div className="public-date-section-header">{formatLongDate(date)}</div>
              {groupedByDate[date].map(prog => {
                const isUnattended = prog.isPast && prog.isLocked && !prog.attended;
                return (
                  <div key={prog.assignmentId || prog.programId} className={getCardClass(prog)}>
                    <div className="public-program-card-header">
                      <span className="public-program-type">
                        {prog.programType}
                        {prog.notes && (
                          <button
                            className="public-notes-link"
                            onClick={() => {
                              const w = window.open('', '_blank');
                              w.document.write(`<!DOCTYPE html><html><head><title>Notes — ${prog.programType}</title><style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;max-width:700px;margin:2rem auto;padding:1rem;color:#333;line-height:1.6;}h2{color:#2e7d32;border-bottom:2px solid #e0e0e0;padding-bottom:0.5rem;}.meta{color:#888;font-size:0.9rem;margin-bottom:1rem;}.notes{white-space:pre-wrap;background:#f8f9fa;padding:1rem;border-radius:8px;border:1px solid #e0e0e0;}</style></head><body><h2>${prog.programType}</h2><div class="meta">${formatLongDate(prog.date)} &bull; ${formatTime(prog.startTime)} &bull; ${prog.durationMinutes} min</div><div class="notes">${prog.notes.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</div></body></html>`);
                              w.document.close();
                            }}
                            title="View notes"
                          >
                            Notes
                          </button>
                        )}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <span className="public-program-time">{(() => {
                          const start = prog.startTime;
                          const dur = parseInt(prog.durationMinutes, 10);
                          if (start && dur) {
                            const [h, m] = start.split(':').map(Number);
                            if (!Number.isNaN(h) && !Number.isNaN(m)) {
                              const total = h * 60 + m + dur;
                              const eh = Math.floor(total / 60) % 24;
                              const em = total % 60;
                              const end = `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
                              return `${formatTime(start)} – ${formatTime(end)} (${dur} min)`;
                            }
                          }
                          return `${formatTime(start)} — ${prog.durationMinutes} min`;
                        })()}</span>
                        {payVisible && <span className="public-pay-badge">${prog.pay.toFixed(2)}</span>}
                        {isUnattended && <span className="public-status-badge unattended">Unattended</span>}
                        {prog.attended && <span className="public-status-badge attended">Attended</span>}
                        {responseEnabled && (prog.response === 'accepted' ? (
                          <span className="public-response-badge accepted">Accepted</span>
                        ) : prog.response === 'rejected' ? (
                          <span className="public-response-badge rejected">Rejected</span>
                        ) : (
                          canRespond(prog) ? (
                          <div className="public-response-buttons">
                            <button
                              className="public-btn-accept"
                              onClick={() => handleRespond(prog, 'accepted')}
                              title="Accept this shift"
                            >&#10003;</button>
                            <button
                              className="public-btn-reject"
                              onClick={() => handleRespond(prog, 'rejected')}
                              title="Reject this shift"
                            >&#10007;</button>
                          </div>
                          ) : null
                        ))}
                        {onAddParticipant && prog.programTypeId && (
                          <button
                            type="button"
                            onClick={() => onAddParticipant(prog.programTypeId)}
                            title={`Allocate a new participant to ${prog.programType}`}
                            style={{
                              padding: '0.3rem 0.7rem',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              background: '#2e7d32',
                              color: 'white',
                              border: '1px solid #1b5e20',
                              borderRadius: 6,
                              cursor: 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            + Add Participant
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => openOtherParticipantsModal(prog)}
                          title={`Add a participant who's enrolled in ${prog.programType} but not normally on this session`}
                          style={{
                            padding: '0.3rem 0.7rem',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            background: 'white',
                            color: '#1565c0',
                            border: '1px solid #1565c0',
                            borderRadius: 6,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Select Other Participants
                        </button>
                      </div>
                    </div>
                    <div className="public-program-details">
                      {prog.location && (
                        <span className="public-program-detail">
                          <strong>Location:</strong> {prog.location}
                        </span>
                      )}
                      {prog.coachCode && (
                        <span className="public-program-detail">
                          <strong>Coach code:</strong>{' '}
                          <span className="public-coach-code" title={prog.coachCodeLabel || ''}>{prog.coachCode}</span>
                          {prog.coachCodeLabel ? ` ${prog.coachCodeLabel}` : ''}
                        </span>
                      )}
                      {prog.qualification && (
                        <span className="public-program-detail">
                          <strong>Role:</strong> {prog.qualification}
                        </span>
                      )}
                    </div>
                    <div className="public-participants">
                      {(() => {
                        const isExpanded = expandedParticipants.has(prog.programId);
                        return (
                      <button
                        type="button"
                        className="public-participants-title"
                        onClick={() => toggleParticipantsExpanded(prog.programId)}
                        title={isExpanded ? 'Hide participants' : 'Show participants'}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          background: 'transparent',
                          border: 'none',
                          padding: '0.4rem 0',
                          cursor: 'pointer',
                          font: 'inherit',
                          color: 'inherit',
                          width: '100%',
                          textAlign: 'left',
                          minHeight: 36
                        }}
                      >
                        <span aria-hidden="true" style={{ fontSize: '0.95rem', display: 'inline-block', transition: 'transform 0.15s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>&#9656;</span>
                        <span>Participants ({prog.participants.length})</span>
                      </button>
                        );
                      })()}
                      {expandedParticipants.has(prog.programId) && (prog.participants.length > 0 ? (
                        <div className="public-participants-list">
                          {prog.participants.map((p, idx) => {
                            const dayReached = isDayOnOrAfter(prog.date);
                            // Status-aware chip colours, mirroring the Coach
                            // Planner badges:
                            //   Financial    -> light green / dark green when attended
                            //   Not financial-> light red   / dark red   when attended
                            //   Unknown      -> light grey  / dark grey  when attended
                            const financial = p.isFinancial === true;
                            const notFinancial = p.isFinancial === false;
                            let chipStyle;
                            if (financial) {
                              chipStyle = p.attended
                                ? { background: '#2e7d32', borderColor: '#1b5e20', color: '#fff' }
                                : { background: '#e8f5e9', borderColor: '#a5d6a7', color: '#2e7d32' };
                            } else if (notFinancial) {
                              chipStyle = p.attended
                                ? { background: '#c62828', borderColor: '#8a1117', color: '#fff' }
                                : { background: '#ffebee', borderColor: '#ef9a9a', color: '#c62828' };
                            } else {
                              chipStyle = p.attended
                                ? { background: '#616161', borderColor: '#424242', color: '#fff' }
                                : { background: '#f5f5f5', borderColor: '#bdbdbd', color: '#555' };
                            }
                            const tapDisabled = !dayReached || prog.isLocked;
                            return (
                              // <label> so tapping anywhere on the chip
                              // toggles attendance, not just the small box.
                              <label
                                key={p.participantId || idx}
                                className="public-participant-chip"
                                style={{ ...chipStyle, borderWidth: 1, borderStyle: 'solid', cursor: tapDisabled ? 'not-allowed' : 'pointer' }}
                                title={notFinancial ? `${p.firstName} ${p.lastName} is NOT financial for this program.` : (prog.isLocked ? 'Day is locked' : dayReached ? 'Mark attendance' : 'Available on or after program day')}
                              >
                                <input
                                  type="checkbox"
                                  className="public-participant-attendance-tick"
                                  checked={p.attended || false}
                                  disabled={tapDisabled}
                                  onChange={() => handleParticipantAttendance(prog, p, p.attended)}
                                />
                                {p.firstName} {p.lastName}
                                {notFinancial && <span style={{ marginLeft: 4, fontWeight: 700 }}>$!</span>}
                              </label>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="public-no-participants">No participants assigned</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          {payVisible && (
            <div className="public-pay-summary">
              <div className="public-pay-item">
                <span>Forecast Pay:</span>
                <strong>${totalPay.toFixed(2)}</strong>
              </div>
              <div className="public-pay-item attended">
                <span>Attended Pay:</span>
                <strong>${attendedPay.toFixed(2)}</strong>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
